import { at } from './model';

/*
  Примерные события «Основы» (spec-3 §11): кнопка «Событие» на верхней полосе пускает их сюда, а не прямо
  в помощника. Сначала событие слышат экраны: экран, который обработал его сам, отменяет его
  (preventDefault) — например, идущее обслуживание cn338 доводит себя до конца на «Операция завершилась».
  Не отменённое уходит оболочке (Shell): она ставит признаки (счётчик колокольчика, точка на закрепе, тост)
  и сообщает помощнику. Настоящие источники — поток уведомлений, события сервисов, долгие операции —
  дело чата ядра (spec-3 §16).
*/

export type SampleKind = 'notification' | 'done' | 'ask' | 'answer';

const channel = new EventTarget();
/** Не отменённое экраном событие — оболочке. */
const FALLBACK = 'fallback';

/** Экран слушает примерное событие; вернуть — отписка. */
export function onSample(kind: SampleKind, handle: (e: Event) => void): () => void {
  channel.addEventListener(kind, handle);
  return () => channel.removeEventListener(kind, handle);
}

/** Оболочка ловит то, что не обработали экраны. */
export function onSampleFallback(handle: (kind: SampleKind) => void): () => void {
  const listener = (e: Event) => handle((e as CustomEvent<SampleKind>).detail);
  channel.addEventListener(FALLBACK, listener);
  return () => channel.removeEventListener(FALLBACK, listener);
}

/** Пустить событие: сначала экранам, не отменённое — оболочке. */
export function emitSample(kind: SampleKind) {
  const handled = !channel.dispatchEvent(new CustomEvent(kind, { cancelable: true }));
  if (!handled) channel.dispatchEvent(new CustomEvent(FALLBACK, { detail: kind }));
}

/* ----- Образцы событий: ключи и цепочки якорей (первый видимый — цель) ----- */

/**
 * «Нужен ответ»: INC-2189 ждёт подтверждения. Цепочка — «следующий шаг» к нему, откуда бы ни смотрели:
 * сам INC-2189 (строка на главной, строка в списке проблем, шапка его вкладки) → его вкладка, если открыта →
 * пункт «Проблемы» в меню «Инфраструктуры» → закреп «Инфраструктура» в главном меню.
 */
export const ASK_KEY = 'ask:INC-2189';
export const ASK_PATH = at('/apps/infra/incident/INC-2189');
export const ASK_ANCHORS = [
  'obj.INC-2189',
  `tab:main:${ASK_PATH}`,
  `tab:side:${ASK_PATH}`,
  'zone.problems',
  'nav.app:infra',
];

/** «Операция завершилась»: обслуживание cn338 — панель на узле, вкладка узла, закреп приложения. */
export const OP_KEY = 'op.host-338';
export const OP_PATH = at('/apps/infra/node/host-338');
export const OP_ANCHORS = [OP_KEY, `tab:main:${OP_PATH}`, `tab:side:${OP_PATH}`, 'nav.app:infra'];
/** Сколько идёт примерная операция, прежде чем завершиться, когда обслуживание не запущено: ядро плавно синее. */
export const SAMPLE_OP_MS = 2400;

/** «Пришло уведомление»: колокольчик в главном меню. */
export const NOTIFY_ANCHOR = 'nav.notifications';
